/**
 * 悬赏猎杀数据
 */

import type { PlayerState } from '../engine/Player';
import { REALMS } from '../data/realms';
import { ZONES } from '../data/zones';

export interface Bounty {
  id: string;
  enemyName: string;
  enemyHp: number;
  enemyAttack: number;
  enemyDefense: number;
  zoneId: string;          // 出没区域
  zoneName: string;
  timeLimitMinutes: number; // 限时
  assignedAt: number;       // 发布时间
  expReward: number;
  stoneReward: number;
  herbReward: number;
  coreReward: number;
}

/** 悬赏敌人模板 */
const BOUNTY_TEMPLATES = [
  { name: '赤焰蟒', power: 2.0, time: 120, exp: 300, stones: 200, herb: 3, core: 1, zones: ['misty_valley', 'bamboo_forest'] },
  { name: '幽冥狼王', power: 2.5, time: 90, exp: 500, stones: 300, herb: 0, core: 3, zones: ['spirit_beast_ridge', 'dark_forest'] },
  { name: '血魔老祖', power: 3.0, time: 60, exp: 800, stones: 500, herb: 0, core: 5, zones: ['demon_swamp', 'dragon_abyss'] },
  { name: '上古兽魂', power: 3.5, time: 120, exp: 1500, stones: 800, herb: 5, core: 8, zones: ['ancient_battlefield', 'celestial_peak'] },
];

/** 生成悬赏 */
export function generateBounty(player: PlayerState): Bounty | null {
  const realmIdx = REALMS.findIndex((r) => r.levels.some((l) => l.key === player.realmKey));
  const available = BOUNTY_TEMPLATES.filter((t) => {
    // 只生成玩家能去的区域的悬赏
    return realmIdx >= 0; // all available for now
  });
  if (available.length === 0) return null;

  const tmpl = available[Math.floor(Math.random() * available.length)];
  const zoneId = tmpl.zones[Math.floor(Math.random() * tmpl.zones.length)];

  // 根据境界缩放敌人强度
  const scale = 1 + realmIdx * 0.3;
  const baseHp = 100 * Math.pow(1.5, realmIdx);

  return {
    id: `bounty_${Date.now()}`,
    enemyName: tmpl.name,
    enemyHp: Math.floor(baseHp * tmpl.power),
    enemyAttack: Math.floor((10 + realmIdx * 8) * tmpl.power),
    enemyDefense: Math.floor((5 + realmIdx * 5) * tmpl.power),
    zoneId,
    zoneName: ZONES.find((z) => z.id === zoneId)?.name ?? zoneId,
    timeLimitMinutes: tmpl.time,
    assignedAt: Date.now(),
    expReward: Math.floor(tmpl.exp * scale),
    stoneReward: Math.floor(tmpl.stones * scale),
    herbReward: tmpl.herb,
    coreReward: tmpl.core,
  };
}

/** 悬赏是否过期 */
export function isBountyExpired(bounty: Bounty): boolean {
  return Date.now() - bounty.assignedAt > bounty.timeLimitMinutes * 60 * 1000;
}

/** 剩余时间描述 */
export function bountyTimeLeft(bounty: Bounty): string {
  const remaining = bounty.timeLimitMinutes * 60 * 1000 - (Date.now() - bounty.assignedAt);
  if (remaining <= 0) return '已过期';
  const mins = Math.floor(remaining / 60000);
  if (mins >= 60) return `${Math.floor(mins / 60)}h${mins % 60}m`;
  return `${mins}分钟`;
}
